import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MapService } from '../../../../../shared/services/map.service';
import { getLayerDefaults, MAP_VIEW_BY_COLORS } from '../../../components/maps/map.constants';

/** One row in the bid-item side panel / one geometry color assignment. */
export interface MappedReportBidItem {
  bid_item_id: string;
  item: string;
  description: string;
  color: string;
  // Summed quantity + unit across this bid item's geometries (label / panel use).
  quantity: number;
  uom: string;
}

/**
 * Right-pane map for the mapped-report viewer.
 *
 * Reuses the proven <app-sites-location-map> renderer in single-day mode. That
 * component owns its own MapView and reads its geometry / marker data from the
 * shared MapService BehaviorSubjects. This pane is a focused feeder: it pushes
 * the instance-detail as-built GeoJSON features (from /v1/mapped-report) into
 * MapService.siteReportGeometries and the instance's site into
 * initiateSiteLocationsMap (for centering). The features ARE the scope, so no
 * other project data can leak onto the map.
 */
@Component({
  selector: 'app-mapped-report-map-pane',
  templateUrl: './mapped-report-map-pane.component.html',
  styleUrls: ['./mapped-report-map-pane.component.scss'],
  standalone: false,
})
export class MappedReportMapPaneComponent implements OnChanges, OnDestroy {
  // As-built GeoJSON features for this form instance (from the detail endpoint).
  @Input() geometries: GeoJSON.Feature[] | null = null;
  // Instance site, used for centering when feature extent can't be derived.
  @Input() site: { latitude: number; longitude: number } | null = null;

  // Single-day mode defaults — all layers on so as-builts render in the
  // non-fullscreen pane (mirrors DailyReportMapContainerComponent).
  readonly layerSettings = getLayerDefaults('singleDay');

  // bid_item ids present in this form — feeds the selection-highlight input so
  // the scoped items read as "selected" on the reused renderer.
  selectedBidItemIds: Set<string> = new Set<string>();

  // Unique bid items in this form (sorted by item number), each with its
  // deterministic color. Drives both the side-panel swatches and the per-
  // geometry color fed to the renderer — same palette/ordering as the main
  // project map's "view by bid item" mode (MAP_VIEW_BY_COLORS keyed by index).
  bidItems: MappedReportBidItem[] = [];

  // Bid item currently hovered in the side panel; bound to the renderer's
  // [hoveredBidItemId] so the matching geometry highlights and the rest fade.
  hoveredBidItemId: string | null = null;

  constructor(private mapService: MapService) {}

  // Shape the renderer's [bidItemsWithGeometries] expects (id/item/description).
  get bidItemsWithGeometries(): Array<{ id: string; item: string; description: string }> {
    return this.bidItems.map(b => ({ id: b.bid_item_id, item: b.item, description: b.description }));
  }

  onBidItemRowEnter(bidItemId: string): void {
    this.hoveredBidItemId = bidItemId;
  }

  onBidItemRowLeave(): void {
    this.hoveredBidItemId = null;
  }

  get hasGeometries(): boolean {
    return !!this.geometries && this.geometries.length > 0;
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.hasGeometries) {
      this.feedMap();
    } else {
      // No geometries (e.g. switched to a form without mapped features): clear
      // the derived panel state so a previous form's rows don't linger.
      this.bidItems = [];
      this.hoveredBidItemId = null;
    }
  }

  ngOnDestroy(): void {
    // Clear the shared feed so this form's scoped geometry/selection does not
    // leak into the next map view that subscribes to the singleton MapService.
    this.mapService.siteReportGeometries.next({
      geometries: [],
      sitesWithGeometries: [],
      siteColorMap: {},
      layerSettings: this.layerSettings,
    });
    this.mapService.initiateSiteLocationsMap.next([]);
    this.mapService.selectedBidItemIds.next([]);
    this.mapService.selectedItemData.next(null);
    this.bidItems = [];
    this.hoveredBidItemId = null;
  }

  private feedMap(): void {
    const features = this.geometries ?? [];
    const siteLat = this.site?.latitude;
    const siteLon = this.site?.longitude;

    // Feed the site so the renderer can center when no feature extent is found.
    if (siteLat != null && siteLon != null) {
      this.mapService.initiateSiteLocationsMap.next([
        {
          site_id: 'mapped-report-site',
          site_name: '',
          longitude: siteLon,
          latitude: siteLat,
          isFiltered: true,
        },
      ]);
    }

    // Derive the unique bid-item list (by bid_item_id) and assign each a
    // deterministic color from the shared MAP_VIEW_BY_COLORS palette, keyed by
    // its index in the item-number-sorted list. This mirrors the main project
    // map's "view by bid item" coloring so panel swatches, the geometries here,
    // and the main map all agree.
    const bidItemMap = new Map<
      string,
      { bid_item_id: string; item: string; description: string; quantity: number; uom: string }
    >();
    features.forEach(f => {
      const props: any = f?.properties || {};
      const bidItemId = props.bid_item_id ?? null;
      if (!bidItemId) return;
      const existing = bidItemMap.get(bidItemId);
      if (!existing) {
        bidItemMap.set(bidItemId, {
          bid_item_id: bidItemId,
          item: props.item || '',
          description: props.description || '',
          quantity: Number(props.quantity) || 0,
          uom: props.uom || '',
        });
      } else {
        // Same bid item across multiple geometries — sum the quantities.
        existing.quantity += Number(props.quantity) || 0;
        if (!existing.uom && props.uom) existing.uom = props.uom;
      }
    });

    this.bidItems = Array.from(bidItemMap.values())
      .sort((a, b) => a.item.localeCompare(b.item))
      .map((b, index) => ({ ...b, color: MAP_VIEW_BY_COLORS[index % MAP_VIEW_BY_COLORS.length] }));

    const colorByBidItemId: { [bidItemId: string]: string } = {};
    this.bidItems.forEach(b => (colorByBidItemId[b.bid_item_id] = b.color));

    // Convert each GeoJSON Feature into the flattened geometry shape the reused
    // renderer expects: the bare GeoJSON geometry (type + coordinates) plus the
    // per-feature metadata the map uses for symbology / selection. The color is
    // the bid item's assigned palette color (bid_item_id powers hover matching).
    const bidItemIds = new Set<string>();
    const geometries: any[] = features
      .filter(f => !!f?.geometry)
      .map(f => {
        const props: any = f.properties || {};
        const bidItemId = props.bid_item_id ?? null;
        if (bidItemId) bidItemIds.add(bidItemId);
        return {
          ...(f.geometry as any),
          isSiteGeometry: false,
          report_bid_item_id: props.report_bid_item_id ?? null,
          bid_item_id: bidItemId,
          bidItemId,
          color: (bidItemId && colorByBidItemId[bidItemId]) || props.color || '#1036D6',
          bid_item_name: props.description || props.item || '',
          bid_item_code: props.item || '',
          // Quantity + unit drive the hovered-geometry centroid label
          // (unifiedMarkerService.updateAsBuiltLabelLayer reads top-level
          // `quantity` and `bid_item_unit` when labelBy === 'quantity').
          quantity: Number(props.quantity) || 0,
          bid_item_unit: props.uom || '',
        };
      });

    this.selectedBidItemIds = bidItemIds;

    this.mapService.siteReportGeometries.next({
      geometries,
      sitesWithGeometries: [],
      siteColorMap: {},
      layerSettings: this.layerSettings,
    });
  }
}
